"use client";

import React from "react";
import { Command, Keyboard, X } from "lucide-react";

interface ShortcutDef {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  category: string;
}

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
  shortcuts: ShortcutDef[];
}

function ShortcutKey({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[1.75rem] items-center justify-center rounded-md border border-border bg-muted px-1.5 py-0.5 text-[0.6875rem] font-mono font-medium text-muted-foreground shadow-sm">
      {children}
    </kbd>
  );
}

function ShortcutDisplay({ shortcut }: { shortcut: ShortcutDef }) {
  const keys: React.ReactNode[] = [];
  if (shortcut.meta) keys.push(<ShortcutKey key="meta"><Command className="h-3 w-3" /></ShortcutKey>);
  if (shortcut.ctrl) keys.push(<ShortcutKey key="ctrl">Ctrl</ShortcutKey>);
  if (shortcut.shift) keys.push(<ShortcutKey key="shift">Shift</ShortcutKey>);
  if (shortcut.alt) keys.push(<ShortcutKey key="alt">Alt</ShortcutKey>);

  if (shortcut.key === " ") {
    keys.push(<ShortcutKey key="key">Space</ShortcutKey>);
  } else if (shortcut.key === "Escape") {
    keys.push(<ShortcutKey key="key">Esc</ShortcutKey>);
  } else if (shortcut.key === "ArrowUp") {
    keys.push(<ShortcutKey key="key">&uarr;</ShortcutKey>);
  } else if (shortcut.key === "ArrowDown") {
    keys.push(<ShortcutKey key="key">&darr;</ShortcutKey>);
  } else if (shortcut.key === "ArrowLeft") {
    keys.push(<ShortcutKey key="key">&larr;</ShortcutKey>);
  } else if (shortcut.key === "ArrowRight") {
    keys.push(<ShortcutKey key="key">&rarr;</ShortcutKey>);
  } else if (shortcut.key === "Enter") {
    keys.push(<ShortcutKey key="key">&crarr;</ShortcutKey>);
  } else if (shortcut.key === "Tab") {
    keys.push(<ShortcutKey key="key">Tab</ShortcutKey>);
  } else {
    keys.push(<ShortcutKey key="key">{shortcut.key.toUpperCase()}</ShortcutKey>);
  }

  return (
    <div className="flex items-center gap-1.5">
      {keys.map((k, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-muted-foreground/40 text-xs">+</span>}
          {k}
        </React.Fragment>
      ))}
    </div>
  );
}

export function KeyboardShortcutsHelp({ open, onClose, shortcuts }: KeyboardShortcutsHelpProps) {
  if (!open) return null;

  const grouped = shortcuts.reduce(
    (acc, s) => {
      if (!acc[s.category]) acc[s.category] = [];
      acc[s.category].push(s);
      return acc;
    },
    {} as Record<string, ShortcutDef[]>,
  );

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-card rounded-2xl border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Keyboard className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Keyboard Shortcuts</h2>
              <p className="text-xs text-muted-foreground">Press <ShortcutKey>?</ShortcutKey> to toggle this panel</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close shortcuts help"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-5 space-y-5">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                {category}
              </h3>
              <div className="space-y-1.5">
                {items.map((shortcut, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg px-3 py-2 bg-muted/30"
                  >
                    <span className="text-sm text-foreground">{shortcut.description}</span>
                    <ShortcutDisplay shortcut={shortcut} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-border/60 bg-muted/20">
          <p className="text-xs text-muted-foreground text-center">
            Press <ShortcutKey>?</ShortcutKey> to toggle this panel &middot; <ShortcutKey>Esc</ShortcutKey> to close
          </p>
        </div>
      </div>
    </div>
  );
}
